import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DataTable } from '../../components/Tables';
import PageHeader from '../shared/PageHeader';
import { citiesApi, statesApi, countriesApi, extractList } from '../../api/facades';

const CitiesListPage: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [countryId, setCountryId] = useState<number | ''>('');
  const [stateId, setStateId] = useState<number | ''>('');

  const { data: countriesRes } = useQuery({
    queryKey: ['countries'],
    queryFn: () => countriesApi.list({ perPage: 200 }),
  });
  const countries = extractList(countriesRes);

  const { data: statesRes } = useQuery({
    queryKey: ['states', countryId],
    queryFn: () =>
      statesApi.list({ countryId: typeof countryId === 'number' ? countryId : undefined, perPage: 200 }),
    enabled: typeof countryId === 'number',
  });
  const states = extractList(statesRes);

  const { data: res, isLoading } = useQuery({
    queryKey: ['cities', stateId, search],
    queryFn: () =>
      citiesApi.list({
        stateId: typeof stateId === 'number' ? stateId : undefined,
        search: search || undefined,
        perPage: 50,
      }),
  });
  const rows = extractList(res);

  const onCountryChange = (v: string) => {
    const n = v === '' ? '' : Number(v);
    setCountryId(n);
    setStateId('');
  };

  const columns = [
    { key: 'name', label: 'Name' },
    {
      key: 'state',
      label: 'State',
      render: (r: any) => r.state?.name ?? r.state_name ?? '—',
    },
  ];

  return (
    <div>
      <PageHeader title="Cities" subtitle="Reference data. Read-only." />
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        <div className="mb-4 flex flex-wrap gap-4">
        <select
          value={countryId === '' ? '' : countryId}
          onChange={(e) => onCountryChange(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none"
        >
          <option value="">All countries</option>
          {countries.map((c: any) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={stateId === '' ? '' : stateId}
          onChange={(e) => setStateId(e.target.value === '' ? '' : Number(e.target.value))}
          disabled={typeof countryId !== 'number'}
          className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none"
        >
          <option value="">All states</option>
          {states.map((s: any) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <input
          type="search"
          placeholder="Search by city name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-xs rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none"
        />
      </div>
      <DataTable
        columns={columns}
        data={rows}
        loading={isLoading}
        emptyMessage="No cities found."
        onRowClick={(id) => navigate(`/dashboard/settings/cities/${id}`)}
        />
      </div>
    </div>
  );
};

export default CitiesListPage;
